import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Receipt } from 'lucide-react'
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { InvoicesTodoTab } from '@/components/features/invoicing/InvoicesTodoTab'
import { InvoicesLedgerTab } from '@/components/features/invoicing/InvoicesLedgerTab'
import { getTasks } from '@/lib/api/tasks'
import {
  defaultInvoicesView,
  parseInvoicesView,
  todoPillSeverity,
  type InvoicesView,
} from '@/lib/invoicesView'
import type { TasksResponse } from '@/types'
import { cn } from '@/lib/utils'

/**
 * The single finance surface: the "À facturer" worklist (invoices to create)
 * and the "Toutes les factures" ledger (invoices that exist), as two tabs.
 *
 * The tab shell renders immediately so both fetches run in parallel and the
 * ledger stays reachable even if the worklist endpoint is slow or down. With
 * no explicit ?view= the worklist is shown while it loads, then the page
 * settles on the worklist when work is pending, otherwise on the ledger.
 */
export default function InvoicesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [tasks, setTasks] = useState<TasksResponse | null>(null)
  const [tasksLoading, setTasksLoading] = useState(true)
  const [tasksError, setTasksError] = useState<string | null>(null)

  // Generation counter: a stale fetch (unmount, or refresh racing an older
  // request) must not overwrite fresher state.
  const fetchGeneration = useRef(0)

  const loadTasks = useCallback(async () => {
    const generation = ++fetchGeneration.current
    setTasksLoading(true)
    setTasksError(null)
    try {
      const data = await getTasks()
      if (fetchGeneration.current === generation) setTasks(data)
    } catch {
      if (fetchGeneration.current === generation) setTasksError('Impossible de charger les tâches.')
    } finally {
      if (fetchGeneration.current === generation) setTasksLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadTasks()
    const generationAtMount = fetchGeneration.current
    return () => {
      // Invalidate in-flight fetches on unmount
      if (fetchGeneration.current === generationAtMount) fetchGeneration.current++
    }
  }, [loadTasks])

  const activeCount = tasks?.activeTasks.length ?? 0
  const overdueCount = tasks?.activeTasks.filter((t) => t.isOverdue).length ?? 0

  // Explicit ?view= always wins. Otherwise show the worklist while it loads
  // (it is the landing intent for invoice users), then settle on the computed
  // default — pending work stays put, an empty worklist yields to the ledger.
  const explicitView = parseInvoicesView(searchParams.get('view'))
  const view: InvoicesView =
    explicitView ?? (tasksLoading ? 'to-invoice' : defaultInvoicesView(activeCount, tasksError != null))

  const handleViewChange = (v: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('view', v)
        return next
      },
      { replace: true }
    )
    // Re-entering the worklist refreshes it: tasks are computed server-side
    // and the prefilled "Facturer" amounts must not go stale on long dwell.
    if (v === 'to-invoice' && !tasksLoading) void loadTasks()
  }

  const pillSeverity = todoPillSeverity(activeCount, overdueCount)

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <Receipt className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Factures</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Ce qu'il reste à facturer et toutes les factures de votre organisation
          </p>
        </div>

        <Tabs value={view} onValueChange={handleViewChange}>
          <TabsList>
            <TabsTrigger value="to-invoice" data-testid="invoices-page-tab-todo">
              À facturer
              {tasksError != null ? (
                <AlertTriangle
                  className="size-3.5 text-amber-500"
                  aria-label="Impossible de charger les tâches"
                  data-testid="invoices-page-todo-error-dot"
                />
              ) : (
                pillSeverity && (
                  <span
                    className={cn(
                      'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums text-white',
                      pillSeverity === 'overdue' ? 'bg-red-500' : 'bg-amber-500'
                    )}
                    title={
                      pillSeverity === 'overdue'
                        ? `${activeCount} à facturer dont ${overdueCount} en retard`
                        : `${activeCount} à facturer`
                    }
                    data-testid="invoices-page-todo-pill"
                  >
                    {activeCount}
                  </span>
                )
              )}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="invoices-page-tab-all">
              Toutes les factures
            </TabsTrigger>
          </TabsList>

          {/* Both tabs stay mounted (forceMount) so the two fetches run in
              parallel and switching preserves the ledger's filters/page with
              no refetch; the inactive panel is hidden by its data-state. */}
          <TabsContent value="to-invoice" forceMount className="mt-2 data-[state=inactive]:hidden">
            <InvoicesTodoTab data={tasks} loading={tasksLoading} error={tasksError} onRefresh={loadTasks} />
          </TabsContent>
          <TabsContent value="all" forceMount className="mt-2 data-[state=inactive]:hidden">
            <InvoicesLedgerTab />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  )
}
